"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/components/AuthProvider";
import { useModal } from "@/components/modals/ModalProvider";
import { setAuthIntent } from "@/lib/auth/authIntent";
import { getSupportDestinationForRole } from "@/lib/auth/redirects";

export default function SupportAuthLink({ href = "/support", children, ...props }) {
  const { user, role, authStatus, serverAuthAuthority } = useAuth();
  const { openModal } = useModal();
  const router = useRouter();
  const pendingRef = useRef(false);
  const isConfirmedGuest =
    authStatus === "unauthenticated" || serverAuthAuthority === "confirmed_guest";
  const destination = getSupportDestinationForRole(href, role) || href;

  useEffect(() => {
    if (!pendingRef.current) return;
    if (user || authStatus === "authenticated") {
      pendingRef.current = false;
      router.push(destination);
      return;
    }
    if (!isConfirmedGuest) return;
    pendingRef.current = false;
    setAuthIntent({ redirectTo: href, role: "customer" });
    openModal("customer-login", { next: href });
  }, [authStatus, destination, href, isConfirmedGuest, openModal, router, user]);

  const handleClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    if (user || authStatus === "authenticated") return;
    event.preventDefault();
    if (!isConfirmedGuest) {
      pendingRef.current = true;
      return;
    }
    setAuthIntent({ redirectTo: href, role: "customer" });
    openModal("customer-login", { next: href });
  };

  return (
    <Link href={user || authStatus === "authenticated" ? destination : href} prefetch={false} {...props} onClick={handleClick}>
      {children}
    </Link>
  );
}
